import React, { useState, useEffect, useCallback } from 'react';
import { Button, Card, Form, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import CloseButton from 'react-bootstrap/CloseButton';
import DataTypeFactory from '../DataTypeFactory.jsx';
import { TABLE_ATTRIBUTE_TYPES } from '../../../settings/constants/types';
import { getDataTypes } from '../DataTypeConfig.jsx';

// Helper function to find nested attributes
const findNestedAttributes = (attributes, path) => {
    if (!path.length) return attributes;
    const [current, ...rest] = path;
    const currentAttr = attributes[current];
    
    // For OBJECT type attributes, look in config.attributes
    const nestedAttributes = currentAttr.type === 'OBJECT' 
        ? (currentAttr.config?.attributes || currentAttr.attributes || [])
        : (currentAttr.attributes || []);
    
    return rest.length ? findNestedAttributes(nestedAttributes, rest) : nestedAttributes;
};

// Helper function to get attribute path names
const getAttributePathNames = (attributes, path) => {
    if (!path.length) return [];
    const [current, ...rest] = path;
    const currentAttr = attributes[current];
    
    // For OBJECT type attributes, look in config.attributes
    const nestedAttributes = currentAttr.type === 'OBJECT' 
        ? (currentAttr.config?.attributes || currentAttr.attributes || [])
        : (currentAttr.attributes || []);
    
    return [
        { index: current, name: currentAttr.name || `Attribute ${current + 1}` },
        ...getAttributePathNames(nestedAttributes, rest)
    ];
};

// Helper function to update nested attributes
const updateNestedAttributes = (attributes, path, newAttributes) => {
    if (!path.length) return newAttributes;
    const [current, ...rest] = path;
    const newArray = [...attributes];
    
    if (rest.length) {
        // For OBJECT type attributes, update config.attributes
        if (newArray[current].type === 'OBJECT') {
            const currentNestedAttributes = newArray[current].config?.attributes || newArray[current].attributes || [];
            const updatedNestedAttributes = updateNestedAttributes(currentNestedAttributes, rest, newAttributes);
            
            newArray[current] = {
                ...newArray[current],
                config: {
                    ...newArray[current].config,
                    attributes: updatedNestedAttributes
                }
            };
        } else {
            newArray[current] = {
                ...newArray[current],
                attributes: updateNestedAttributes(newArray[current].attributes || [], rest, newAttributes)
            };
        }
    } else {
        // For OBJECT type attributes, update config.attributes
        if (newArray[current].type === 'OBJECT') {
            newArray[current] = {
                ...newArray[current],
                config: {
                    ...newArray[current].config,
                    attributes: newAttributes
                }
            };
        } else {
            newArray[current] = {
                ...newArray[current],
                attributes: newAttributes
            };
        }
    }
    return newArray;
};

// Helper function to validate attribute name
const isValidAttributeName = (name) => {
    return name && name.trim().length > 0;
};

const BreadcrumbNav = ({ pathNames, onNavigate }) => {
    return (
        <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-3">
                <li className="breadcrumb-item">
                    <Button
                        variant="link"
                        className="p-0"
                        onClick={() => onNavigate([])}
                    >
                        Root
                    </Button>
                </li>
                {pathNames.map((item, index) => (
                    <li key={index} className="breadcrumb-item">
                        <Button
                            variant="link"
                            className="p-0"
                            onClick={() => onNavigate(pathNames.slice(0, index + 1).map(p => p.index))}
                        >
                            {item.name}
                        </Button>
                    </li>
                ))}
            </ol>
        </nav>
    );
};

const AttributeEditor = ({ attributes = [], onChange, currentPath = [], onPathChange }) => {
    const [currentAttributes, setCurrentAttributes] = useState([]);
    const [pathNames, setPathNames] = useState([]);
    const [typeOptions, setTypeOptions] = useState([]);
    const [isLoadingTypes, setIsLoadingTypes] = useState(false);

    // Load current attributes and path names
    useEffect(() => {
        console.log('AttributeEditor useEffect - attributes:', attributes);
        console.log('AttributeEditor useEffect - currentPath:', currentPath);
        const attrs = findNestedAttributes(attributes || [], currentPath) || [];
        const names = getAttributePathNames(attributes || [], currentPath);
        console.log('AttributeEditor useEffect - found attributes:', attrs);
        console.log('AttributeEditor useEffect - path names:', names);
        setCurrentAttributes(attrs);
        setPathNames(names);
    }, [attributes, currentPath]);

    // Load type options from API
    useEffect(() => {
        loadTypeOptions();
    }, []);

    const loadTypeOptions = async () => {
        setIsLoadingTypes(true);
        try {
            const options = await getDataTypes();
            setTypeOptions(options);
        } catch (error) {
            console.error('Error loading type options:', error);
            // Fallback to basic types if API fails
            setTypeOptions([
                { label: 'STRING', value: 'STRING' },
                { label: 'NUMBER', value: 'NUMBER' },
                { label: 'BOOLEAN', value: 'BOOLEAN' },
                { label: 'OBJECT', value: 'OBJECT' },
                { label: 'ENUM', value: 'ENUM' },
                { label: 'COLLECTION_OBJECT', value: 'COLLECTION_OBJECT' }
            ]);
        } finally {
            setIsLoadingTypes(false);
        }
    };

    const handleAddAttribute = () => {
        const newAttributes = [...currentAttributes, {
            name: "",
            type: "",
            description: "",
            listAttribute: false,
            attributes: []
        }];
        onChange(updateNestedAttributes(attributes || [], currentPath, newAttributes));
    };

    const handleRemoveAttribute = (index) => {
        const newAttributes = [...currentAttributes];
        newAttributes.splice(index, 1);
        onChange(updateNestedAttributes(attributes || [], currentPath, newAttributes));
    };

    const handleAttributeChange = (index, field, value) => {
        const newAttributes = [...currentAttributes];
        const updatedAttribute = { ...newAttributes[index], [field]: value };
        
        // If type is changing to OBJECT, initialize config structure
        if (field === 'type' && value === 'OBJECT') {
            updatedAttribute.config = {
                ...updatedAttribute.config,
                attributes: updatedAttribute.config?.attributes || updatedAttribute.attributes || []
            };
            // Remove the direct attributes field to prevent duplication
            delete updatedAttribute.attributes;
        }
        
        // If type is changing from OBJECT to something else, move attributes back to direct field
        if (field === 'type' && newAttributes[index].type === 'OBJECT' && value !== 'OBJECT') {
            updatedAttribute.attributes = updatedAttribute.config?.attributes || [];
            delete updatedAttribute.config;
        }
        
        newAttributes[index] = updatedAttribute;
        onChange(updateNestedAttributes(attributes || [], currentPath, newAttributes));
    };

    const handleNavigateToNested = (index) => {
        onPathChange([...currentPath, index]);
    };

    const canAddAttribute = Array.isArray(currentAttributes) &&
        (currentAttributes.length === 0 || currentAttributes.every(attr => attr && isValidAttributeName(attr.name)));

    return (
        <div>
            {currentPath.length > 0 && (
                <BreadcrumbNav
                    pathNames={pathNames}
                    onNavigate={onPathChange}
                />
            )}
            <div className="mb-3">
                {Array.isArray(currentAttributes) && currentAttributes.map((attr, index) => (
                    <div key={index} className="mb-3 p-2 border rounded">
                        <div className="row mb-2">
                            <div className="col-md-4">
                                <input
                                    type="text"
                                    className={`form-control ${!isValidAttributeName(attr?.name) ? 'is-invalid' : ''}`}
                                    placeholder="Attribute Name"
                                    value={attr?.name || ""}
                                    onChange={(e) => handleAttributeChange(index, "name", e.target.value)}
                                />
                                {!isValidAttributeName(attr?.name) && (
                                    <div className="invalid-feedback">
                                        Attribute name is required
                                    </div>
                                )}
                            </div>
                            <div className="col-md-4">
                                <Select
                                    options={typeOptions}
                                    value={{ label: attr?.type || "", value: attr?.type || "" }}
                                    onChange={(e) => handleAttributeChange(index, "type", e.value)}
                                    className="react-select-container"
                                    isLoading={isLoadingTypes}
                                    placeholder={isLoadingTypes ? "Loading types..." : "Select Type"}
                                />
                            </div>
                            <div className="col-md-2">
                                <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => handleRemoveAttribute(index)}
                                >
                                    Remove
                                </Button>
                            </div>
                        </div>
                        <div className="row mb-2">
                            <div className="col-md-8">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Description (optional)"
                                    value={attr?.description || ""}
                                    onChange={(e) => handleAttributeChange(index, "description", e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="row mb-2">
                            <div className="col-md-4">
                                <input
                                    type="checkbox"
                                    name={`listAttribute_${index}`}
                                    id={`listAttribute_${index}`}
                                    checked={attr?.listAttribute || false}
                                    onChange={(e) => handleAttributeChange(index, "listAttribute", e.target.checked)}
                                />
                                <label className='ms-2'>List Attribute</label>
                            </div>
                        </div>
                        {(attr?.type === "OBJECT") && (
                            <div className="mt-2">
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleNavigateToNested(index)}
                                    disabled={!isValidAttributeName(attr?.name)}
                                >
                                    Edit Nested Attributes ({(attr?.config?.attributes || attr?.attributes || []).length})
                                </Button>
                            </div>
                        )}
                    </div>
                ))}
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleAddAttribute}
                    disabled={!canAddAttribute}
                >
                    Add Attribute
                </Button>
            </div>
        </div>
    );
};

export default function ObjectField({ value, onChange, mode, config, field, ...props }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [currentPath, setCurrentPath] = useState([]);
    const { attributes = [], listAttribute } = config || {};
    
    if (mode === "view") {
        const displayValue = typeof value === 'object' ? JSON.stringify(value, null, 2) : value;
        return (
            <div>
                <span className="text-muted">Object</span>
                {value && (
                    <button 
                        type="button" 
                        className="btn btn-sm btn-outline-secondary ms-2"
                        onClick={() => setIsExpanded(!isExpanded)}
                    >
                        {isExpanded ? "Collapse" : "Expand"}
                    </button>
                )}
                {isExpanded && (
                    <pre className="mt-2 p-2 bg-light border rounded">
                        {displayValue}
                    </pre>
                )}
            </div>
        );
    }

    // Handle edit mode for object attributes
    if (mode === "edit" && field?.editAttributes) {
        return (
            <Card className="mt-3">
                <Card.Body>
                    <Row>
                        <Col md={12}>
                            <div className="mb-3">
                                <label>Attributes *</label>
                                <AttributeEditor
                                    attributes={attributes}
                                    onChange={(newAttributes) => {
                                        // Update the config with new attributes
                                        const updatedConfig = {
                                            ...config,
                                            attributes: newAttributes
                                        };
                                        // Call onChange with the updated config
                                        onChange(updatedConfig);
                                    }}
                                    currentPath={currentPath}
                                    onPathChange={setCurrentPath}
                                />
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        );
    }

    // Recursive function to render nested objects
    const renderNestedObject = (attr, attrValue, onChange, level = 0) => {
        const nestedAttributes = attr.config?.attributes || attr.attributes || [];
        const badgeClass = level === 0 ? "bg-info" : level === 1 ? "bg-secondary" : "bg-light text-dark";
        
        return (
            <div className="nested-object">
                <div className="d-flex align-items-center mb-3">
                    <h6 className="mb-0">{attr.name} - Nested Attributes</h6>
                </div>
                <div className="row">
                    {nestedAttributes.map((nestedAttr, nestedIndex) => {
                        const nestedValue = attrValue && typeof attrValue === 'object' ? attrValue[nestedAttr.name] : "";
                        
                        // If nested attribute is also an OBJECT type, render it recursively
                        if (nestedAttr.type === TABLE_ATTRIBUTE_TYPES.OBJECT) {
                            return (
                                <div key={nestedIndex} className="col-12 mb-3">
                                    <label className="form-label small fw-bold d-flex align-items-center">
                                        {nestedAttr.name} <span className={`badge ms-1 ${badgeClass}`}>{nestedAttr.type}</span>
                                    </label>
                                    <div className="nested-object">
                                        {renderNestedObject(nestedAttr, nestedValue, (updatedNestedValue) => {
                                            const updatedValue = {
                                                ...attrValue,
                                                [nestedAttr.name]: updatedNestedValue
                                            };
                                            onChange(updatedValue);
                                        }, level + 1)}
                                    </div>
                                </div>
                            );
                        }
                        
                        // Regular nested attribute (not OBJECT type)
                        return (
                            <div key={nestedIndex} className="col-md-6 mb-3">
                                <label className="form-label small fw-bold">
                                    {nestedAttr.name} <span className={`badge ms-1 ${badgeClass}`}>{nestedAttr.type}</span>
                                </label>
                                <DataTypeFactory
                                    type={nestedAttr.type}
                                    value={nestedValue}
                                    onChange={(nestedValue) => {
                                        const updatedValue = {
                                            ...attrValue,
                                            [nestedAttr.name]: nestedValue
                                        };
                                        onChange(updatedValue);
                                    }}
                                    mode="edit"
                                    config={nestedAttr.config || {}}
                                    options={nestedAttr.type === TABLE_ATTRIBUTE_TYPES.ENUM ? 
                                        (nestedAttr.config?.enumValues || nestedAttr.enumValues || []).map(val => ({ label: val, value: val })) : 
                                        undefined
                                    }
                                    field={nestedAttr}
                                />
                            </div>
                        );
                    })}
                </div>
                {nestedAttributes.length === 0 && (
                    <div className="empty-state">
                        <small>No nested attributes defined for this object</small>
                    </div>
                )}
            </div>
        );
    };

    // Handle list of objects
    if (listAttribute && attributes.length > 0) {
        return (
            <div className="object-list-container">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="text-muted">Object List</span>
                    <button 
                        type="button" 
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => {
                            const currentArray = Array.isArray(value) ? value : [];
                            onChange([...currentArray, {}]);
                        }}
                    >
                        + Add Item
                    </button>
                </div>
                
                {(Array.isArray(value) ? value : [{}]).map((item, itemIndex) => (
                    <div key={itemIndex} className="border rounded p-3 mb-3 bg-white">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0">Item {itemIndex + 1}</h6>
                            {(Array.isArray(value) ? value : []).length > 1 && (
                                <button 
                                    type="button" 
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => {
                                        const currentArray = Array.isArray(value) ? value : [];
                                        const newArray = currentArray.filter((_, index) => index !== itemIndex);
                                        onChange(newArray);
                                    }}
                                >
                                    Remove Item
                                </button>
                            )}
                        </div>
                        
                        <div className="row">
                            {attributes.map((attr, attrIndex) => {
                                const attrValue = item[attr.name] || "";
                                
                                // If attribute is OBJECT type, render it recursively
                                if (attr.type === TABLE_ATTRIBUTE_TYPES.OBJECT) {
                                    return (
                                        <div key={attrIndex} className="col-12 mb-3">
                                            <label className="form-label small fw-bold d-flex align-items-center">
                                                {attr.name} <span className="badge bg-info ms-1">{attr.type}</span>
                                            </label>
                                            {renderNestedObject(attr, attrValue, (updatedAttrValue) => {
                                                const currentArray = Array.isArray(value) ? value : [];
                                                const newArray = [...currentArray];
                                                newArray[itemIndex] = {
                                                    ...newArray[itemIndex],
                                                    [attr.name]: updatedAttrValue
                                                };
                                                onChange(newArray);
                                            })}
                                        </div>
                                    );
                                }
                                
                                // Regular attribute (not OBJECT type)
                                return (
                                    <div key={attrIndex} className="col-md-6 mb-3">
                                        <label className="form-label">
                                            {attr.name} <span className="badge bg-info">{attr.type}</span>
                                        </label>
                                        <DataTypeFactory
                                            type={attr.type}
                                            value={attrValue}
                                            onChange={(attrValue) => {
                                                const currentArray = Array.isArray(value) ? value : [];
                                                const newArray = [...currentArray];
                                                newArray[itemIndex] = {
                                                    ...newArray[itemIndex],
                                                    [attr.name]: attrValue
                                                };
                                                onChange(newArray);
                                            }}
                                            mode="edit"
                                            config={attr.config}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    // Handle single object
    if (attributes.length > 0) {
        return (
            <div className="nested-object">
                <div className="d-flex align-items-center mb-3">
                    <h6 className="mb-0">{field?.name || 'Object'} - Nested Attributes</h6>
                </div>
                <div className="row">
                    {attributes.map((attr, attrIndex) => {
                        const attrValue = value?.[attr.name] || "";
                        
                        // If attribute is OBJECT type, render it recursively
                        if (attr.type === TABLE_ATTRIBUTE_TYPES.OBJECT) {
                            return (
                                <div key={attrIndex} className="col-12 mb-3">
                                    <label className="form-label small fw-bold d-flex align-items-center">
                                        {attr.name} <span className="badge bg-info ms-1">{attr.type}</span>
                                    </label>
                                    {renderNestedObject(attr, attrValue, (updatedAttrValue) => {
                                        onChange({
                                            ...value,
                                            [attr.name]: updatedAttrValue
                                        });
                                    })}
                                </div>
                            );
                        }
                        
                        // Regular attribute (not OBJECT type)
                        return (
                            <div key={attrIndex} className="col-md-6 mb-3">
                                <label className="form-label small fw-bold">
                                    {attr.name} <span className="badge bg-info ms-1">{attr.type}</span>
                                </label>
                                <DataTypeFactory
                                    type={attr.type}
                                    value={attrValue}
                                    onChange={(attrValue) => {
                                        onChange({
                                            ...value,
                                            [attr.name]: attrValue
                                        });
                                    }}
                                    mode="edit"
                                    config={attr.config}
                                />
                            </div>
                        );
                    })}
                </div>
                {attributes.length === 0 && (
                    <div className="empty-state">
                        <small>No nested attributes defined for this object</small>
                    </div>
                )}
            </div>
        );
    }
    
    // Fallback to JSON editing
    return (
        <textarea 
            value={typeof value === 'object' ? JSON.stringify(value, null, 2) : (value || "")} 
            onChange={e => {
                try {
                    const parsed = JSON.parse(e.target.value);
                    onChange(parsed);
                } catch (error) {
                    onChange(e.target.value);
                }
            }} 
            className="form-control"
            placeholder="Enter JSON object"
            rows={5}
            {...props}
        />
    );
} 